// src/context/CapabilitiesContext.js
// Single shared context for tier capabilities system.
// Wrap the app with <CapabilitiesProvider> to fetch capabilities once at startup
// and provide tier/capabilities to all components that need feature gating.

import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { getCapabilities } from "../api/capabilities";

const CapabilitiesContext = createContext(null);

export const CapabilitiesProvider = ({ children }) => {
  const [activeTier, setActiveTier] = useState("executive");
  const [activeTierLabel, setActiveTierLabel] = useState("TradePilot Executive");
  const [baseTier, setBaseTier] = useState("executive");
  const [baseTierLabel, setBaseTierLabel] = useState("TradePilot Executive");
  const [capabilities, setCapabilities] = useState({});
  const [availableTiers, setAvailableTiers] = useState([]);
  const [tierLabels, setTierLabels] = useState({});
  const [entitlementApplied, setEntitlementApplied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchCapabilities = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getCapabilities();
      const nextActiveTier = data.active_tier || "executive";
      const nextBaseTier = data.base_tier || nextActiveTier;
      const labels = data.tier_labels || {};

      setActiveTier(nextActiveTier);
      setBaseTier(nextBaseTier);
      setCapabilities(data.capabilities || {});
      setAvailableTiers(data.available_tiers || []);
      setTierLabels(labels);
      setActiveTierLabel(
        data.active_tier_label || labels[nextActiveTier] || `TradePilot ${String(nextActiveTier).replace(/^./, (c) => c.toUpperCase())}`
      );
      setBaseTierLabel(
        data.base_tier_label || labels[nextBaseTier] || `TradePilot ${String(nextBaseTier).replace(/^./, (c) => c.toUpperCase())}`
      );
      setEntitlementApplied(Boolean(data.entitlement_applied));
    } catch (err) {
      setError(err?.message || "Failed to load capabilities.");
      // Fallback to executive tier on error (most permissive)
      setActiveTier("executive");
      setBaseTier("executive");
      setActiveTierLabel("TradePilot Executive");
      setBaseTierLabel("TradePilot Executive");
      setCapabilities({});
      setAvailableTiers([]);
      setTierLabels({});
      setEntitlementApplied(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCapabilities();
  }, [fetchCapabilities]);

  useEffect(() => {
    const onEntitlementUpdated = () => {
      fetchCapabilities();
    };
    window.addEventListener("tradepilot-entitlement-updated", onEntitlementUpdated);
    return () => {
      window.removeEventListener("tradepilot-entitlement-updated", onEntitlementUpdated);
    };
  }, [fetchCapabilities]);

  return (
    <CapabilitiesContext.Provider
      value={{
        activeTier,
        activeTierLabel,
        baseTier,
        baseTierLabel,
        capabilities,
        availableTiers,
        tierLabels,
        entitlementApplied,
        loading,
        error,
        refresh: fetchCapabilities,
      }}
    >
      {children}
    </CapabilitiesContext.Provider>
  );
};

export const useCapabilities = () => {
  const context = useContext(CapabilitiesContext);
  if (!context) {
    throw new Error("useCapabilities must be used within CapabilitiesProvider");
  }
  return context;
};
